import { useEffect, useState } from "react";
import { api, API, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StarRating } from "@/components/StarRating";
import { Paperclip, Trash2, FileText, Send, CornerDownRight, Reply, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

function Composer({ onSubmit, withRating, compact, placeholder }) {
  const [text, setText] = useState("");
  const [rating, setRating] = useState(0);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post(`/files/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ id: res.data.id, name: res.data.original_filename, content_type: res.data.content_type });
      }
      await onSubmit({ text, rating: withRating ? rating || null : null, attachments: uploaded });
      setText(""); setRating(0); setFiles([]);
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-2">
      {withRating && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Rating:</span>
          <StarRating value={rating} onChange={setRating} testid="comment-rating-input" />
        </div>
      )}
      <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} rows={compact ? 2 : 2} data-testid="comment-text-input" />
      {files.length > 0 && <div className="text-xs text-muted-foreground">{files.map((f) => f.name).join(", ")}</div>}
      <div className="flex items-center justify-between">
        <label className="inline-flex items-center gap-1 text-sm text-secondary cursor-pointer" data-testid="comment-attach-label">
          <Paperclip size={15} /> Attach
          <input type="file" multiple className="hidden" onChange={(e) => setFiles([...files, ...Array.from(e.target.files)])} />
        </label>
        <Button size="sm" onClick={submit} disabled={busy} data-testid="comment-submit-btn">
          <Send size={14} className="mr-1" /> {busy ? "Posting..." : "Post"}
        </Button>
      </div>
    </div>
  );
}

function Attachments({ list, token }) {
  if (!list?.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {list.map((a) => (
        <a key={a.id} href={`${API}/files/${a.id}/download?auth=${token || ""}`} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1 rounded-md bg-white border border-slate-200 px-2 py-1 text-xs text-secondary hover:underline"
           data-testid={`attachment-${a.id}`}>
          <FileText size={12} /> {a.name}
        </a>
      ))}
    </div>
  );
}

export function CommentThread({ entityType, entityId, withRating = false }) {
  const [comments, setComments] = useState([]);
  const [replyTo, setReplyTo] = useState(null);

  const load = () => api.get(`/comments`, { params: { entity_type: entityType, entity_id: entityId } })
    .then((r) => setComments(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityId]);

  const post = async ({ text, rating, attachments }, parent_id = null) => {
    await api.post(`/comments`, { entity_type: entityType, entity_id: entityId, text, rating, attachments, parent_id });
    setReplyTo(null); load();
  };
  const del = async (id) => { await api.delete(`/comments/${id}`); load(); };
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const startEdit = (c) => { setEditId(c.id); setEditText(c.text); };
  const saveEdit = async (c) => {
    await api.put(`/comments/${c.id}`, { text: editText, rating: c.rating ?? null });
    setEditId(null); load();
  };

  const token = "";
  const roots = comments.filter((c) => !c.parent_id);
  const childrenOf = (id) => comments.filter((c) => c.parent_id === id);

  const Item = ({ c, isReply }) => (
    <div className={`rounded-lg border border-slate-200 bg-slate-50 p-3 ${isReply ? "ml-6" : ""}`} data-testid={`comment-${c.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isReply && <CornerDownRight size={13} className="text-slate-400" />}
          <span className="text-sm font-semibold">{c.author}</span>
          {c.rating ? <StarRating value={c.rating} readOnly size={13} /> : null}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => startEdit(c)} className="text-slate-400 hover:text-primary" data-testid={`comment-edit-${c.id}`}><Pencil size={13} /></button>
          <button onClick={() => del(c.id)} className="text-slate-400 hover:text-red-500" data-testid={`comment-delete-${c.id}`}><Trash2 size={14} /></button>
        </div>
      </div>
      {editId === c.id ? (
        <div className="mt-2 space-y-2" data-testid={`comment-edit-box-${c.id}`}>
          <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={2} data-testid={`comment-edit-input-${c.id}`} />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => saveEdit(c)} data-testid={`comment-save-${c.id}`}><Check size={13} className="mr-1" /> Save</Button>
            <Button size="sm" variant="outline" onClick={() => setEditId(null)}><X size={13} className="mr-1" /> Cancel</Button>
          </div>
        </div>
      ) : (
        <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{c.text}</p>
      )}
      <Attachments list={c.attachments} token={token} />
      {!isReply && (
        <button onClick={() => setReplyTo(replyTo === c.id ? null : c.id)} className="mt-2 inline-flex items-center gap-1 text-xs text-secondary hover:underline" data-testid={`reply-btn-${c.id}`}>
          <Reply size={12} /> Reply
        </button>
      )}
      {replyTo === c.id && (
        <div className="mt-2 rounded-lg border border-slate-200 bg-white p-2" data-testid={`reply-composer-${c.id}`}>
          <Composer onSubmit={(payload) => post(payload, c.id)} withRating={false} compact placeholder="Write a reply..." />
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="comment-thread">
      <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
        {roots.length === 0 && <p className="text-sm text-muted-foreground">No comments yet. Start the discussion.</p>}
        {roots.map((c) => (
          <div key={c.id} className="space-y-2">
            <Item c={c} isReply={false} />
            {childrenOf(c.id).map((r) => <Item key={r.id} c={r} isReply />)}
          </div>
        ))}
      </div>
      <div className="border-t border-slate-200 pt-3">
        <Composer onSubmit={(payload) => post(payload, null)} withRating={withRating} placeholder="Add pros / cons, rationale, notes..." />
      </div>
    </div>
  );
}
